import React, { useState, useEffect, useContext } from 'react';
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  message, 
  Card, 
  Popconfirm,
  Select,
  Tag,
  Switch,
  InputNumber,
  Row,
  Col,
  Tabs
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import api from '../../utils/axios';
import DepartmentSelect from '../../components/DepartmentSelect';
import { useAuth } from '../../contexts/AuthContext';
import initManager from '../../utils/initManager';

const { Option } = Select;
const { TextArea } = Input;

const OnboardingTasksSettings = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form] = Form.useForm();
  const [filterDepartmentId, setFilterDepartmentId] = useState(null);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [modalDeptId, setModalDeptId] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [currentTaskCategory, setCurrentTaskCategory] = useState('basic');
  const [clearAllModalVisible, setClearAllModalVisible] = useState(false);

  // 获取任务列表
  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await api.get('/onboarding-tasks');
      console.log('任务数据:', response.data);
      // 确保数据是数组格式
      const tasksData = Array.isArray(response.data) ? response.data : [];
      setTasks(tasksData);
      
      // 缓存数据并标记为已初始化
      initManager.setCachedData('onboardingTasks', tasksData);
      initManager.setInitialized('onboardingTasks');
    } catch (error) {
      console.error('获取入职任务列表失败:', error);
      message.error('获取入职任务列表失败');
      setTasks([]); // 出错时设置为空数组
    } finally {
      setLoading(false);
    }
  };

  // 获取部门列表
  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      // 确保数据是数组格式
      const departmentsData = Array.isArray(response.data) ? response.data : [];
      setDepartments(departmentsData);
      
      // 缓存部门数据
      initManager.setCachedData('departments', departmentsData);
      initManager.setInitialized('departments');
    } catch (error) {
      console.error('获取部门列表失败:', error);
      setDepartments([]); // 出错时设置为空数组
    }
  };

  // 获取岗位列表
  const fetchPositions = async () => {
    try {
      const response = await api.get('/positions');
      // 确保数据是数组格式
      const positionsData = Array.isArray(response.data) ? response.data : [];
      setPositions(positionsData);
      
      // 缓存岗位数据
      initManager.setCachedData('positions', positionsData);
      initManager.setInitialized('positions');
    } catch (error) {
      console.error('获取岗位列表失败:', error);
      setPositions([]); // 出错时设置为空数组
    }
  };

  useEffect(() => {
    // 检查是否已初始化，避免重复获取数据
    if (!initManager.isInitialized('onboardingTasks')) {
      fetchTasks();
    } else {
      // 使用缓存的数据
      const cachedData = initManager.getCachedData('onboardingTasks');
      if (cachedData) {
        setTasks(cachedData);
      } else {
        // 如果缓存为空，重新获取
        fetchTasks();
      }
    }
    
    // 部门数据也需要检查
    if (!initManager.isInitialized('departments')) {
      fetchDepartments();
    } else {
      const cachedDeptData = initManager.getCachedData('departments');
      if (cachedDeptData) {
        setDepartments(cachedDeptData);
      } else {
        fetchDepartments();
      }
    }
    
    // 岗位数据也需要检查
    if (!initManager.isInitialized('positions')) {
      fetchPositions();
    } else {
      const cachedPosData = initManager.getCachedData('positions');
      if (cachedPosData) {
        setPositions(cachedPosData);
      } else {
        fetchPositions();
      }
    }
  }, []);

  // 根据Tab和部门筛选任务
  useEffect(() => {
    if (!Array.isArray(tasks)) {
      setFilteredTasks([]);
      return;
    }
    
    let filtered = tasks;
    
    // 首先按Tab分类筛选
    if (activeTab !== 'all') {
      filtered = filtered.filter(task => task.task_category === activeTab);
    }
    
    // 然后按部门筛选（如果选择了部门）
    if (filterDepartmentId) {
      filtered = filtered.filter(task => task.department_id === filterDepartmentId);
    }
    
    setFilteredTasks(filtered);
  }, [tasks, filterDepartmentId, activeTab]);

  // 添加任务
  const handleAdd = () => {
    setEditingTask(null);
    form.resetFields();
          // 设置新增任务的默认值
      form.setFieldsValue({
        task_type: 'text',
        task_category: 'basic',
        sort_order: 0,
        department_id: undefined,
        position_id: undefined
      });
    setModalDeptId(null); // 新增时清空部门
    setCurrentTaskCategory('basic'); // 重置任务分类状态
    setModalVisible(true);
  };

  // 编辑任务
  const handleEdit = (record) => {
    setEditingTask(record);
    form.setFieldsValue({
      name: record.name,
      description: record.description,
      task_type: record.task_type,
      task_category: record.task_category || 'basic',
      department_id: record.department_id,
      position_id: record.position_id,
      sort_order: record.sort_order
    });
    setModalDeptId(record.department_id || null);
    setCurrentTaskCategory(record.task_category || 'basic'); // 设置当前任务分类状态
    setModalVisible(true);
  };

  // 删除任务
  const handleDelete = async (id) => {
    try {
      await api.delete(`/onboarding-tasks/${id}`);
      message.success('入职任务删除成功');
      fetchTasks();
    } catch (error) {
      console.error('删除入职任务失败:', error);
      message.error(error.response?.data?.error || '删除入职任务失败');
    }
  };

  // 清理所有任务（仅管理员）
  const handleClearAllTasks = async () => {
    try {
      const response = await fetch('/api/onboarding-tasks/clear-all', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const result = await response.json();
        message.success(result.message);
        fetchTasks(); // 刷新任务列表
      } else {
        const errorData = await response.json();
        message.error(errorData.message || '清理失败');
      }
    } catch (error) {
      console.error('清理所有任务失败:', error);
      message.error('清理失败');
    }
  };

  // 处理部门筛选
  const handleDepartmentFilter = (departmentId) => {
    setFilterDepartmentId(departmentId);
  };

  // 清除筛选
  const handleClearFilter = () => {
    setFilterDepartmentId(null);
    setActiveTab('basic');
  };

  // 处理任务分类变化
  const handleTaskCategoryChange = (category) => {
    console.log('任务分类变化:', category);
    setCurrentTaskCategory(category);
    
    if (category === 'basic') {
      // 基础任务：清空部门和岗位
      form.setFieldsValue({ 
        department_id: undefined, 
        position_id: undefined
      });
      setModalDeptId(null);
    } else if (category === 'department') {
      // 部门任务：清空岗位
      form.setFieldsValue({ position_id: undefined });
    }
    // 岗位任务：保持当前选择
  };



  // 提交表单
  const handleSubmit = async (values) => {
    try {
      // 调试信息
      console.log('🔍 提交的表单数据:', values);
      console.log('🔍 排序字段值:', values.sort_order);
      console.log('🔍 排序字段类型:', typeof values.sort_order);
      
      // 验证必填字段
      if (!values.name || !values.task_type || !values.task_category) {
        message.error('请填写必填字段');
        return;
      }
      
      // 验证任务分类与绑定关系
      if (values.task_category === 'department' && !values.department_id) {
        message.error('部门任务必须选择部门');
        return;
      }
      
      if (values.task_category === 'position' && !values.position_id) {
        message.error('岗位任务必须选择岗位');
        return;
      }
      
      // 确保数据完整性，过滤掉无效值
      const submitData = {
        name: values.name,
        description: values.description || '',
        task_type: values.task_type,
        task_category: values.task_category,
        sort_order: values.sort_order || 0
      };
      
      // 根据任务分类设置相应的字段，只发送有效的值
      if (values.task_category === 'department') {
        if (values.department_id && values.department_id !== undefined && values.department_id !== '') {
          submitData.department_id = parseInt(values.department_id);
        }
      }
      
      if (values.task_category === 'position') {
        if (values.position_id && values.position_id !== undefined && values.position_id !== '') {
          submitData.position_id = parseInt(values.position_id);
        }
      }
      
      console.log('原始表单数据:', values);
      console.log('最终提交数据:', submitData);
      console.log('数据类型检查:', {
        department_id: values.department_id,
        position_id: values.position_id,
        department_id_type: typeof values.department_id,
        position_id_type: typeof values.position_id
      });
      
      if (editingTask) {
        await api.put(`/onboarding-tasks/${editingTask.id}`, submitData);
        message.success('入职任务更新成功');
      } else {
        await api.post('/onboarding-tasks', submitData);
        message.success('入职任务创建成功');
      }
      setModalVisible(false);
      fetchTasks();
    } catch (error) {
      console.error('保存入职任务失败:', error);
      message.error(error.response?.data?.error || '保存入职任务失败');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '任务名称',
      dataIndex: 'name',
      key: 'name',
      width: 150
    },
    {
      title: '任务类型',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 100,
      render: (type) => (
        <Tag color={type === 'text' ? 'blue' : 'green'}>
          {type === 'text' ? '文本' : '文件'}
        </Tag>
      )
    },
    {
      title: '任务分类',
      dataIndex: 'task_category',
      key: 'task_category',
      width: 100,
      render: (category) => {
        const colorMap = {
          'basic': 'blue',
          'department': 'orange',
          'position': 'green'
        };
        const textMap = {
          'basic': '基础任务',
          'department': '部门任务',
          'position': '岗位任务'
        };
        return (
          <Tag color={colorMap[category] || 'default'}>
            {textMap[category] || category}
          </Tag>
        );
      }
    },
    {
      title: '所属部门',
      dataIndex: 'department_name',
      key: 'department_name',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '所属岗位',
      dataIndex: 'position_name',
      key: 'position_name',
      width: 120,
      render: (text) => text || '-'
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-'
    },

    {
      title: '排序',
      dataIndex: 'sort_order',
      key: 'sort_order',
      width: 80
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      render: (text) => new Date(text).toLocaleString('zh-CN')
    },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 150,
      align: 'right',
      render: (_, record) => (
        <Space size="small" style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            size="small"
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个入职任务吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              size="small"
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card
        title="入职任务管理"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchTasks}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              新增任务
            </Button>

            {(user?.role === 'admin' || user?.role === 'super_admin') && (
              <Button 
                danger 
                icon={<DeleteOutlined />}
                onClick={() => setClearAllModalVisible(true)}
              >
                清理所有任务
              </Button>
            )}
          </Space>
        }
      >
        {/* Tab分类和筛选区域 */}
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ marginBottom: 16 }}
          items={[
            {
              key: 'basic',
              label: (
                <span>
                  基础任务
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    {tasks.filter(t => t.task_category === 'basic').length}
                  </Tag>
                </span>
              )
            },
            {
              key: 'department',
              label: (
                <span>
                  部门任务
                  <Tag color="orange" style={{ marginLeft: 8 }}>
                    {tasks.filter(t => t.task_category === 'department').length}
                  </Tag>
                </span>
              )
            },
            {
              key: 'position',
              label: (
                <span>
                  岗位任务
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    {tasks.filter(t => t.task_category === 'position').length}
                  </Tag>
                </span>
              )
            },
            {
              key: 'all',
              label: (
                <span>
                  全部任务
                  <Tag color="default" style={{ marginLeft: 8 }}>
                    {tasks.length}
                  </Tag>
                </span>
              )
            }
          ]}
        />

        {/* 筛选区域 */}
        <div style={{ marginBottom: 16, padding: '16px', background: '#fafafa', borderRadius: '6px' }}>
          <Row gutter={16} align="middle">
            <Col>
              <span style={{ marginRight: 8 }}>部门筛选：</span>
            </Col>
            <Col>
              <Select
                placeholder="选择部门筛选"
                style={{ width: 200 }}
                allowClear
                value={filterDepartmentId}
                onChange={handleDepartmentFilter}
                disabled={activeTab === 'basic'}
              >
                {departments.map(dept => (
                  <Option key={dept.id} value={dept.id}>
                    {dept.name}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col>
              <Button onClick={handleClearFilter} disabled={!filterDepartmentId && activeTab !== 'all'}>
                清除筛选
              </Button>
            </Col>
            <Col>
              <span style={{ color: '#666' }}>
                共 {filteredTasks.length} 条任务
                {filterDepartmentId && `（已筛选）`}
                {activeTab !== 'all' && `（${activeTab === 'basic' ? '基础' : activeTab === 'department' ? '部门' : '岗位'}任务）`}
              </span>
            </Col>
          </Row>
        </div>

        <Table
          columns={columns}
          dataSource={filteredTasks}
          rowKey="id"
          loading={loading}
          scroll={{ x: 1200 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`
          }}
        />
      </Card>

      {/* 任务编辑模态框 */}
      <Modal
        title={editingTask ? '编辑入职任务' : '新增入职任务'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >



        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          onValuesChange={(changedValues, allValues) => {
            console.log('表单值变化:', changedValues, '所有值:', allValues);
            // 如果任务分类发生变化，更新状态
            if (changedValues.task_category !== undefined) {
              setCurrentTaskCategory(changedValues.task_category);
            }
          }}
        >
          <Form.Item
            name="name"
            label="任务名称"
            rules={[{ required: true, message: '请输入任务名称' }]}
          >
            <Input placeholder="请输入任务名称" />
          </Form.Item>

          <Form.Item
            name="task_type"
            label={
              <span>
                任务类型
                {editingTask && <Tag color="red" style={{ marginLeft: 8, fontSize: '11px' }}>不可编辑</Tag>}
              </span>
            }
            rules={[{ required: true, message: '请选择任务类型' }]}
            extra={editingTask ? "编辑模式下不可修改，确保已有工单数据一致性" : ""}
            style={editingTask ? { opacity: 0.6, backgroundColor: '#f5f5f5' } : {}}
          >
            <Select disabled={!!editingTask}>
              <Option value="text">文本类型</Option>
              <Option value="file">文件类型</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="task_category"
            label={
              <span>
                任务分类
                {editingTask && <Tag color="red" style={{ marginLeft: 8, fontSize: '11px' }}>不可编辑</Tag>}
              </span>
            }
            rules={[{ required: true, message: '请选择任务分类' }]}
            extra={editingTask ? "编辑模式下不可修改，确保已有工单数据一致性" : ""}
            style={editingTask ? { opacity: 0.6, backgroundColor: '#f5f5f5' } : {}}
          >
            <Select 
              disabled={!!editingTask}
              onChange={editingTask ? undefined : (value) => handleTaskCategoryChange(value)}
            >
              <Option value="basic">基础任务</Option>
              <Option value="position">岗位任务</Option>
              <Option value="department">部门任务</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="任务描述"
          >
            <TextArea rows={3} placeholder="请输入任务描述" />
          </Form.Item>

          <Form.Item
            name="department_id"
            label={
              <span>
                所属部门
                {editingTask && <Tag color="red" style={{ marginLeft: 8, fontSize: '11px' }}>不可编辑</Tag>}
              </span>
            }
            dependencies={['task_category']}
            rules={[
              ({ getFieldValue }) => {
                const taskCategory = getFieldValue('task_category');
                if (taskCategory === 'department') {
                  return { required: true, message: '部门任务必须选择部门' };
                }
                return { required: false };
              }
            ]}
            hidden={currentTaskCategory === 'basic'}
            extra={editingTask ? "编辑模式下不可修改，确保已有工单数据一致性" : ""}
            style={editingTask ? { opacity: 0.6, backgroundColor: '#f5f5f5' } : {}}
          >
            <DepartmentSelect 
              placeholder="请选择部门" 
              allowClear 
              disabled={!!editingTask}
              onChange={editingTask ? undefined : (val) => {
                setModalDeptId(val || null);
                // 部门变化时清空岗位
                form.setFieldsValue({ position_id: undefined });
              }}
            />
          </Form.Item>

          <Form.Item
            name="position_id"
            label={
              <span>
                所属岗位
                {editingTask && <Tag color="red" style={{ marginLeft: 8, fontSize: '11px' }}>不可编辑</Tag>}
              </span>
            }
            dependencies={['task_category']}
            rules={[
              ({ getFieldValue }) => {
                const taskCategory = getFieldValue('task_category');
                if (taskCategory === 'position') {
                  return { required: true, message: '岗位任务必须选择岗位' };
                }
                return { required: false };
              }
            ]}
            hidden={currentTaskCategory === 'basic' || currentTaskCategory === 'department'}
            extra={editingTask ? "编辑模式下不可修改，确保已有工单数据一致性" : ""}
            style={editingTask ? { opacity: 0.6, backgroundColor: '#f5f5f5' } : {}}
          >
            <Select 
              placeholder="请选择岗位" 
              allowClear
              disabled={!!editingTask}
            >
              {(positions || [])
                .filter(p => (modalDeptId ? p.department_id === modalDeptId : true))
                .map(position => (
                  <Option key={position.id} value={position.id}>
                    {position.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>



          <Form.Item
            name="sort_order"
            label="排序"
            initialValue={0}
            extra="数字越小，优先级越高，用于控制任务在列表中的显示顺序"
          >
            <InputNumber 
              min={0} 
              max={9999}
              placeholder="请输入排序号" 
              style={{ width: '100%' }} 
            />
          </Form.Item>

          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button onClick={() => setModalVisible(false)}>
                取消
              </Button>
              <Button type="primary" htmlType="submit">
                {editingTask ? '更新' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 清理所有任务确认Modal */}
      <Modal
        title="清理所有任务"
        open={clearAllModalVisible}
        onCancel={() => setClearAllModalVisible(false)}
        onOk={() => {
          handleClearAllTasks();
          setClearAllModalVisible(false);
        }}
        okText="确定清理"
        cancelText="取消"
        okType="danger"
        width={500}
      >
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <DeleteOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>
            确认清理所有入职任务
          </div>
          <div style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
            此操作将删除所有入职任务和相关的任务记录
          </div>
          <div style={{ color: '#ff4d4f', fontSize: '14px', fontWeight: 'bold' }}>
            ⚠️ 此操作不可逆！
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OnboardingTasksSettings; 